import React, { useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ASPECT_BUCKETS, AspectBucket, BUCKET_DIMENSIONS } from '../../utils/channelAspect';

/** Tiny proportional rectangle visualizing a bucket's shape. */
export const AspectGlyph: React.FC<{ bucket: AspectBucket }> = ({ bucket }) => {
  const d = BUCKET_DIMENSIONS[bucket];
  const scale = 15 / Math.max(d.width, d.height);
  return (
    <span
      aria-hidden
      style={{
        width: 18,
        height: 18,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      <span
        style={{
          width: Math.max(7, Math.round(d.width * scale)),
          height: Math.max(7, Math.round(d.height * scale)),
          borderRadius: 3,
          border: '1.5px solid currentColor',
          opacity: 0.75,
          display: 'block',
        }}
      />
    </span>
  );
};

/** Amber star marking the recommended (universal-default) bucket. */
const RecommendedStar: React.FC = () => (
  <svg
    role="img"
    width="12"
    height="12"
    viewBox="0 0 24 24"
    fill="currentColor"
    style={{ marginLeft: 'auto', flexShrink: 0, color: 'var(--signal-feed)' }}
  >
    <title>Recommended</title>
    <path d="M12 2l2.9 6.26 6.6.7-4.95 4.5 1.4 6.54L12 16.77 6.05 20l1.4-6.54L2.5 8.96l6.6-.7L12 2z" />
  </svg>
);

/**
 * Aspect-ratio picker menu (.tend-menu list): shape glyph + ratio label per
 * row, with a star signposting `recommended` (the universal default).
 * Deliberately narrow — shared by the Design panel's Aspect Ratios section
 * today and, in Phase 2, the tended-content overlay's aspect switcher
 * (where the star shows what they moved off of). Positioning and any
 * click-away backdrop belong to the caller via `style`/siblings; `footer`
 * takes extra items (e.g. a reset-to-universal row).
 */
const AspectRatioMenu: React.FC<{
  current: AspectBucket | string;
  recommended?: AspectBucket | string | null;
  onPick: (bucket: AspectBucket) => void;
  footer?: React.ReactNode;
  style?: React.CSSProperties;
}> = ({ current, recommended, onPick, footer, style }) => (
  <div className="tend-menu tend-surface" role="menu" style={{ minWidth: 122, ...style }}>
    {ASPECT_BUCKETS.map((b) => (
      <button
        key={b}
        type="button"
        className={`tend-menu-item${b === current ? ' is-active' : ''}`}
        onClick={() => onPick(b)}
      >
        <AspectGlyph bucket={b} />
        <span>{b}</span>
        {b === recommended && <RecommendedStar />}
      </button>
    ))}
    {footer}
  </div>
);

export interface AspectAnchorRect {
  top: number;
  bottom: number;
  left: number;
  right: number;
}

/**
 * Portal variant: renders the menu (plus a click-away backdrop) on
 * document.body with fixed positioning measured against `anchorRect` and
 * clamped to the viewport — immune to any modal / scroll-container /
 * overflow-hidden clipping, which is why every host (Design panel now,
 * tended-content cards in Phase 2) should prefer this over in-place
 * absolute positioning. Hosts that close on outside clicks must flag the
 * open state (e.g. the Design panel's pickerOpenRef) since the portal
 * lives outside their DOM.
 */
export const AspectRatioMenuPopover: React.FC<{
  anchorRect: AspectAnchorRect;
  onClose: () => void;
  current: AspectBucket | string;
  recommended?: AspectBucket | string | null;
  onPick: (bucket: AspectBucket) => void;
  footer?: React.ReactNode;
  zIndex?: number;
}> = ({ anchorRect, onClose, zIndex = 1000000, ...menuProps }) => {
  // Default sits above every host layer — the Design panel (70), profile
  // sheets (90), and the TENDED content modal (999999 in TendButton.tsx).
  // A menu with its own click-away backdrop should always be topmost.
  const boxRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  // Measure the rendered menu, then place it: below the anchor when it
  // fits, above when there's room there instead, clamped into the
  // viewport (10px margins) either way.
  useLayoutEffect(() => {
    const el = boxRef.current;
    if (!el) return;
    const m = el.getBoundingClientRect();
    const margin = 10;
    let left = anchorRect.left;
    if (left + m.width > window.innerWidth - margin) {
      left = Math.max(margin, anchorRect.right - m.width);
    }
    let top = anchorRect.bottom + 6;
    if (top + m.height > window.innerHeight - margin) {
      const above = anchorRect.top - 6 - m.height;
      top = above >= margin ? above : Math.max(margin, window.innerHeight - margin - m.height);
    }
    setPos({ top, left });
  }, [anchorRect]);

  return createPortal(
    <>
      <div style={{ position: 'fixed', inset: 0, zIndex }} onClick={onClose} />
      <div
        ref={boxRef}
        style={{
          position: 'fixed',
          zIndex: zIndex + 1,
          top: pos?.top ?? anchorRect.bottom + 6,
          left: pos?.left ?? anchorRect.left,
          visibility: pos ? undefined : 'hidden',
        }}
      >
        <AspectRatioMenu {...menuProps} />
      </div>
    </>,
    document.body
  );
};

export default AspectRatioMenu;
